import React from 'react'
import './invoiceStyle.css'
import i18n from '../../i18n'
import { linkDefaultParams } from '../../utils/linkDefaultParams'
import { getIndikacniSkupiny } from '../utils/payments'
import PaymentQrCode from './PaymentQrCode'

interface iInvoice {
  values: any
  submission: any
  varSymbol: string
  regCislo?: string
}

function formatAmountCZK(amount: number) {
  const isEnglish = i18n.language === 'en';

  const formattedNumber = new Intl.NumberFormat(isEnglish ? 'en-US' : 'cs-CZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(amount);

  return isEnglish
    ? `${formattedNumber.replace(/,/g, ' ')} CZK`
    : `${formattedNumber} Kč`;
}

const Invoice = ({ values, submission, varSymbol, regCislo }: iInvoice) => {

  const d = new Date(submission?.createdAt)
  const datumVytvoreni = d.getDate() + '. ' + (d.getMonth() + 1) + '. ' + d.getFullYear()

  const platby = values ? Object.values(values.platby) : []

  const polozky = platby.map((item: any) => ({
    kod: item.definice.kod,
    uhrada: item.definice.uhrada,
    pocet: item.pocet,
    vlastniUhrada: item.vlastniUhrada,
  }))

  const celkem = values?.platba?.celkovaCenaPlateb

  const paltbaFirst = platby[0] as any
  const platbaDetail = paltbaFirst ? paltbaFirst.definice : null
  const oblast = values.platba.oblast
  const { konsymbol, iban, ucet, identifikator: identifikatorStr, skupina, id: oblast_id } = oblast ?? {}

  const identifikator = parseInt(identifikatorStr)
  const maint_title = identifikator !== 8 ? i18n.language !== 'en' ? platbaDetail?.nazevKategorie : platbaDetail?.nazevKategorieEn : ''
  const payment2_text = identifikator !== 8 ? i18n.t('invoice.seznamVybranychZadosti') : i18n.t('invoice.kodPlatby')
  const showBox = identifikator !== 8
  const headerbonus = skupina === 2

  let header_conf = ''
  let agent_contact = ''
  switch (identifikator) {
    case 8:
      header_conf = i18n.t('invoice.predpisPlatbyRocniUdrzovaciPlatby')
      agent_contact = i18n.t('invoice.zmocnenaOsoba')
      break
    case 7:
      header_conf = i18n.t('invoice.predpisPlatbySpravnihoPoplatku')
      agent_contact = i18n.t('invoice.zmocnenaKontaktniOsoba')
      break

    case 65:
      header_conf = i18n.t('invoice.predpisPlatbyNahradVydajuPrijetiZadosti')
      agent_contact = i18n.t('invoice.zmocnenaOsoba')
      break

    case 75:
      header_conf = i18n.t('invoice.predpisPlatbySpravnihoPoplatkuPrijetiZadosti')
      agent_contact = i18n.t('invoice.zmocnenaOsoba')
      break

    case 9:
      if (platbaDetail?.typ === '01') {
        header_conf = i18n.t('invoice.predpisPlatbyPokuty')
      } else {
        header_conf = i18n.t('invoice.predpisPlatbyNakladuRizeni')
      }
      agent_contact = i18n.t('invoice.zmocnenec')
      break
    default:
      header_conf = i18n.t('invoice.predpisPlatbyNahradyVydeju')
      agent_contact = i18n.t('invoice.zmocnenaKontaktniOsoba')
      break
  }

  const udaje = [] as { label: string, data: any }[]
  const addUdaj = (label: string, data: any) => udaje.push({ label, data })

  if (values) {

    if (identifikator === 65 || identifikator === 75 || identifikator === 7) {
      addUdaj(
        i18n.t('invoice.typZadosti'),
        <>{polozky.map((p: any) => p.kod).join(', ')}</>
      )
    }
    if (identifikator === 8) {
      addUdaj(
        i18n.t('invoice.typRocniUdrzovaciPlatby'),
        i18n.t('invoice.rocniUdrzovaciPlatbaRegistrace')
      )
    }

    /* LP */

    if (identifikator === 2 || identifikator === 65 || identifikator === 75 || identifikator === 7 || identifikator === 8) { //???

      if (oblast_id !== 14 && values.lecivyPripravek) {
          const { nazev, forma, sila, lekforma } = values.lecivyPripravek.udajeLP

        const pocetSukKod = values.lecivyPripravek.pocetSuklKodu ? ('(počet dalších kódů SÚKL: ' +values.lecivyPripravek.pocetSuklKodu + ')')   : ''
        addUdaj(
          i18n.t('invoice.nazevLekovaFormaSila'),
          nazev ? <>{nazev}, {forma}, {sila} {pocetSukKod}</> : <>{lekforma}</>
        )
      }

      if (identifikator === 65 || identifikator === 75) {
        const kodSukl = values.lecivyPripravek?.kodSukl
        if (kodSukl) {
          addUdaj(
            i18n.t('invoice.kodSUKL'),
            <>{kodSukl}</>
          )
        }
      }

      if (identifikator === 65 || identifikator === 75 || identifikator === 8) {
        if (regCislo) {
          addUdaj(
            i18n.t('invoice.registracniCislo'),
            <>{regCislo}</>
          )
        }
      }

      if (identifikator !== 8) {

        const latka = values.lecivyPripravek?.udajeLP?.latka
        if (latka) {
          addUdaj(
            i18n.t('invoice.lecivaLatka'),
            <>{latka}</>
          )
        }

        const mrp = values.lecivyPripravek?.mrpCislo
        if (mrp) {
          addUdaj(
            i18n.t('invoice.mrpCislo'),
            <>{mrp}</>
          )
        }

        const dalsiLP = values.lecivyPripravek?.pocetDalsichLP
        if (dalsiLP) {
          addUdaj(
            i18n.t('invoice.pocetDalsichLP'),
            <>{dalsiLP}</>
          )
        }
        const indSkupina = values.lecivyPripravek?.udajeLP?.indSkupina
        if (indSkupina) {
          const indikacniSkupiny = getIndikacniSkupiny()
          const ind = indikacniSkupiny.filter((skupina: any) => skupina.value === indSkupina)[0]
          addUdaj(
            i18n.t('invoice.indikacniSkupina'),
            <>{ind.label}</>
          )
        }
        if (values.lecivyPripravek?.datumPodani) {
          const datum = new Date(values.lecivyPripravek?.datumPodani)
          addUdaj(
            i18n.t('invoice.predpokladaneDatumPodani'),
            <>{datum.getDate() + '. ' + (datum.getMonth() + 1) + '. ' + datum.getFullYear()}</>
          )
        }
      }
    }
    if (identifikator === 9) {
      //pokuty
      addUdaj(
        i18n.t('invoice.ucastnikRizeni'),
        <>{values.zadatel?.nazev}, IČ: {values.zadatel?.IC} </>
      )

      addUdaj(
        i18n.t('invoice.spisovaZnacka'),
        <>{values.spisovaZnacka}</>
      )
      addUdaj(
        i18n.t('invoice.pracovnikSUKL'),
        <>{values.jmenoZadatele}</>
      )
    } else {
      if (!((identifikator === 65) || (identifikator === 75) || (identifikator === 7) || (identifikator === 8))) {
        if (values.specifikace) {
          addUdaj(
            i18n.t('invoice.specifikaceObsahu'),
            <>{values.specifikace}</>
          )
        }
      }

      const { nazev, IC, adresa } = values.zadatel ?? {}
      addUdaj(
        i18n.t('invoice.zadatel'),
        <>
          {nazev}, {adresa?.ulice} {adresa?.cisloDomu}, {adresa?.psc} {adresa?.obec}, {adresa?.stat},
          {!!IC && <> IČ: {IC}</>}
        </>
      )
    }

    if (values.kontaktniOsoba?.poverenaOsoba) {

      const { titulPred, jmeno, prijmeni, titulZa } = values.kontaktniOsoba?.poverenaOsoba || {}

      const { nazev, IC, adresa } = values.kontaktniOsoba?.zadatel ?? values.zadatel ?? {}
      addUdaj(
        `${agent_contact}:`,
        <>
          {titulPred} {jmeno} {prijmeni} {titulZa}
          <br/>
          {nazev && <>{nazev}, </>}
          {adresa &&
            <>
              {adresa?.ulice} {adresa?.cisloDomu}, {adresa?.psc} {adresa?.obec}, {adresa?.stat},{' '}
            </>
          }
          {!!IC && <> IČ: {IC}</>}
        </>
      )
    }

    const { email, telefon, datovaSchranka, fax } = values.kontakt ?? {}

    addUdaj(
      i18n.t('invoice.kontakt'),
      <>
        {email && <>e-mail: {email}, </>}
        {telefon && <>tel: {telefon}, </>}
        {fax && <>fax: {fax}, </>}
        {datovaSchranka && <>ds: {datovaSchranka}, </>}
      </>
    )

    //poznamka k sukl labelu - *) zruseno
    // TODO colspan 2
    // if (identifikator === 65 || identifikator === 75) {
    //   addUdaj(
    //     '*) vyplní se v případě, že je v této fázi již přidělen',
    //     <></>
    //   )
    // }

    if (identifikator === 8) {
      addUdaj(
        i18n.t('invoice.zaObdobi'),
        <> {values.lecivyPripravek?.zaObdobi}</>
      )
    }

    if ([1, 2, 3, 4, 5].includes(oblast_id)) {
      if (values.zadatel?.jeMikroZadatel === 'ano') {
        addUdaj(
          i18n.t('invoice.sleva50'),
          i18n.t('invoice.popisSlevy')
        )
      }
    }

    // u pokut ne
    if(identifikator !== 9 && values.zadatel.iban){
      addUdaj(
        i18n.t('invoice.ibanPlatce'),
        values.zadatel.iban
      )
    }
  }

  const url = window.location.protocol + '//' + window.location.host + '/form/' + submission?.widget

  const backlink = values ? linkDefaultParams(url, {
    'zadatel/nazev': values.zadatel?.nazev,
    'zadatel/IC': values.zadatel?.IC,
    'zadatel/adresa/ulice': values.zadatel?.adresa?.ulice,
    'zadatel/adresa/cisloDomu': values.zadatel?.adresa?.cisloDomu,
    'zadatel/adresa/psc': values.zadatel?.adresa?.psc,
    'zadatel/adresa/obec': values.zadatel?.adresa?.obec,
    'zadatel/adresa/stat': values.zadatel?.adresa?.stat,
    'kontakt/email': values.kontakt?.email,
    'kontakt/telefon': values.kontakt?.telefon,
    'kontakt/fax': values.kontakt?.fax,
  }) : ''

  return (
    <div className="invoice-wrap">
      <div className="noprint noprint-center">
        <div className="nop">
          <p>{i18n.t('invoice.uspesneVlozeniDat')}</p>
          <p>{i18n.t('invoice.navratNaFormular')} <a
            href={backlink}>{i18n.t('invoice.zpetNaFormular')}</a>, {i18n.t('invoice.vyjimkaKategorieZadosti')}</p>
        </div>
      </div>


      <br/>
      <p className="V13BblackFaT invoice-text-center">
        <strong>{i18n.t('invoice.nazevSukl')}</strong>
        <br/>
        <span className="V11BblackFaT">
      {i18n.t('invoice.adresaSukl')}<br/>
          {i18n.t('invoice.kontaktSukl')}<br/>
          {i18n.t('invoice.icSukl')}, {i18n.t('invoice.cisloUctu')} {ucet}, {i18n.t('invoice.bankovniSpojeni')}<br/>
      IBAN: {iban}, BIC (SWIFT): CNBACZPP<br/>
    </span>
      </p>

      <p className="V15BblackFaT invoice-text-center">
        <strong dangerouslySetInnerHTML={{ __html: header_conf }}/>
        {headerbonus &&
          <span className="V13BblackFaT"><br/>&nbsp;</span>
        }
      </p>


      {showBox &&
        <table className="invoice-table" cellSpacing="0" cellPadding="0">
          <tbody>
          <tr>
            <td style={{ width: 450 }}>&nbsp;</td>
            <td style={{ width: 250 }}>
              <table className="invoice-table--full ram" cellSpacing="0" cellPadding="5">
                <tbody>
                <tr className="V11BblackFaT">
                  <td>
                    <i>{i18n.t('invoice.upozorneniVnitrniPotreby')}</i><br/>
                    {i18n.t('invoice.cisloZadosti')}<br/>
                    {i18n.t('invoice.datumZadosti')}<br/>
                    {i18n.t('invoice.prikazKUhrade')}<br/>
                    {i18n.t('invoice.utvar')}<br/>
                    {i18n.t('invoice.doDatabazeZadano')} {datumVytvoreni}
                  </td>
                </tr>
                </tbody>
              </table>
            </td>
          </tr>
          </tbody>
        </table>
      }


      <br/>
      <table className="invoice-table" cellSpacing="0" cellPadding="0">
        <tbody>
        <tr>
          <td align="center">
            <br/>
            <div className="V13BblackFaT"><strong>{maint_title}</strong></div>
            <br/>
          </td>
        </tr>
        </tbody>
      </table>

      <table className="invoice-table" cellSpacing="1" cellPadding="5">
        <tbody className="V11BblackFaT">

        {/*<tr>*/}
        {/*  <td colSpan="2">*/}
        {/*    {platbaDetail?.kategorie}*/}
        {/*    {platbaDetail?.podkategorie ? <><br/> {platbaDetail?.podkategorie}</> : ''}*/}
        {/*  </td>*/}
        {/*</tr>*/}

        {udaje.map((item, index) =>
          <tr key={index}>
            <td>{item.label}</td>
            <td className="invoice-data-value"><strong>{item.data}</strong></td>
          </tr>
        )}
        </tbody>
      </table>
      <br/>

      <table className="invoice-table" cellSpacing="0" cellPadding="5">
        <tbody className="V11BblackFaT">
        <tr>
          <td className="invoice-payment-description"><strong>{payment2_text}:</strong></td>
          <td className="invoice-payment-total" align="center">{i18n.t('invoice.castkaKUhrade')}</td>
        </tr>
        </tbody>
      </table>

      <table className="invoice-table invoice-table--light ram" cellSpacing="0" cellPadding="5">
        <tbody className="V15BblackFaT">
        <tr>
          <td className="invoice-payment-description ram2">
            <table className="invoice-payment-items" cellSpacing="0" cellPadding="0">
              <tbody className="V13BblackFaT invoice-text-right">
              {polozky.map((item: any, index: number) =>
                <tr key={index}>
                  <td align="left"><b>{item.kod}</b></td>

                  <td align="right"><strong>{formatAmountCZK(item.vlastniUhrada || item.uhrada)}</strong></td>

                  {(oblast_id === 14 && values?.lecivyPripravek?.poTerminu === 'ano') &&
                    <td> × 1,5</td>
                  }
                  {item.pocet > 1 && item.uhrada &&
                    <td>
                      {item.kod === 'L-009' && i18n.t('invoice.pocetLahvicek')}
                      {item.kod === 'L-007' && i18n.t('invoice.pocetSarzi')}
                      {(item.kod !== 'L-009' && item.kod !== 'L-007') && 'x '}
                      {item.pocet}
                    </td>
                  }
                </tr>
              )}
              </tbody>
            </table>
          </td>
          <td className="invoice-payment-total" align="center" valign="middle"><strong>{formatAmountCZK(celkem)}</strong></td>
        </tr>
        </tbody>
      </table>

      <br/>
      <table className="invoice-table" cellSpacing="0" cellPadding="0">
        <tbody className="V11BblackFaT">
        <tr>
          <td><strong>{i18n.t('invoice.identifikacniUdajePlatby')}</strong></td>
        </tr>
        </tbody>
      </table>
      <table className="invoice-table payment-details" cellSpacing="0" cellPadding="0">
        <tbody>
        <tr>
          <td className="payment-details__data" valign="top">
            <table className="invoice-table--full" cellSpacing="1" cellPadding="5">
              <tbody className="V11BblackFaT invoice-text-center">
              <tr>
                <td className="payment-details__column">{i18n.t('invoice.cisloUctuSukl')}</td>
                <td className="payment-details__column">{i18n.t('invoice.variabilniSymbol')}</td>
                <td className="payment-details__column payment-details__column--last">{i18n.t('invoice.konstantniSymbol')}</td>
              </tr>
              </tbody>
            </table>
            <table className="invoice-table--full invoice-table--light ram" cellSpacing="0" cellPadding="5">
              <tbody className="V11BblackFaT invoice-text-center">
              <tr>
                <td className="payment-details__column payment-details__value ram2"><b>{ucet}</b></td>
                <td className="payment-details__column payment-details__value ram2"><b>{varSymbol}</b></td>
                <td className="payment-details__column payment-details__column--last payment-details__value ram2"><b>{konsymbol}</b></td>
              </tr>
              <tr>
                <td className="payment-details__column payment-details__value ram2"><b>IBAN: {iban}</b></td>
                <td className="payment-details__column payment-details__value ram2"><b>BIC (SWIFT): CNBACZPP</b></td>
                <td className="payment-details__column payment-details__column--last payment-details__value ram2"><b>{konsymbol}</b></td>
              </tr>
              </tbody>
            </table>
          </td>
          <td className="payment-details__qr" valign="middle">
            <PaymentQrCode
              iban={iban}
              amount={celkem}
              variableSymbol={varSymbol}
              constantSymbol={konsymbol}
            />
          </td>
        </tr>
        </tbody>
      </table>
      <br/>
      <table className="invoice-table" cellSpacing="0" cellPadding="0">
        <tbody>
        <tr className="V11BblackFaT">
          <td>{i18n.t('invoice.datum')}<br/><br/></td>
          <td style={{ width: 250 }} align="left">
            <hr style={{ width: 150 }}/>
          </td>
          <td>&nbsp;&nbsp;</td>
          <td>{i18n.t('invoice.podpisPlatce')}<br/><br/></td>
          <td align="left">
            <hr style={{ width: 250 }}/>
          </td>
        </tr>
        </tbody>
      </table>
      <table style={{ width: 620, margin: '0 auto' }} cellSpacing="0" cellPadding="10" className="ram">
        <tbody>
        <tr>
          <td>
            <table style={{ width: 621, margin: '0 auto' }} cellSpacing="2" cellPadding="0">
              <tbody className="V11BblackFaT">
              <tr style={{ textAlign: 'center' }}>
                <td colSpan={3}>
                  <span className="V13BblackFaT"><strong>{i18n.t('invoice.potvrzeniPrijmuPlatby')}</strong></span><br/>
                  <span className="V11BblackFaT"><i>{i18n.t('invoice.upozorneniVnitrniPotreby')}</i><br/><br/></span>
                </td>
              </tr>
              <tr>
                <td style={{ width: 207 }}></td>
                <td style={{ width: 207 }}>{i18n.t('invoice.cisloPoklDokladu')}</td>
                <td style={{ width: 207 }}>{i18n.t('invoice.datumPlatby')}</td>
              </tr>
              <tr>
                <td></td>
                <td>{i18n.t('invoice.cisloVypisu')}</td>
                <td>{i18n.t('invoice.datumVypisu')}</td>
              </tr>
              <tr>
                <td colSpan={3}>
                  <br/><br/><br/><br/>
                </td>
              </tr>
              <tr>
                <td align="center">{i18n.t('invoice.zaSpravnost')}</td>
                <td align="center">{i18n.t('invoice.datum')}</td>
                <td align="center">{i18n.t('invoice.podpis')}</td>
              </tr>
              </tbody>
            </table>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  )

}

export default Invoice
